import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, AlertTriangle, PenLine, Link2, RotateCcw } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useScopeStore, findNodeInTree } from '@/stores/scope';
import { ConfirmDialog } from '@/components/Modal';

/** `/settings/visitor-count` (来客数管理タブ、v0.3 S-5)
 *  設計: docs/design/v03-visitor-count-generalization.md §5-1 */

type VisitorCountMethod = 'menu_derive' | 'manual_input' | 'external_sync';
type DeriveRule = 'sum_main_only' | 'sum_all_included' | 'max_included';
type VisitorCountConfig = { method: VisitorCountMethod; derive_rule: DeriveRule };

type TenantSettings = { visitor_count_config?: VisitorCountConfig };

type CategoryRole = {
  menu_category_id: string;
  category_code: string | null;
  category_name: string;
  visitor_count_role: 'main' | 'included' | 'excluded' | null;
  is_override: boolean;
  inherited_from_node_name: string | null;
};

type SourcePriorityResponse = { node_id: string; source_priority: string[]; is_default: boolean };

const METHOD_LABELS: Record<VisitorCountMethod, string> = {
  menu_derive: 'メニューの販売数から算出',
  manual_input: '来客数を取り込む (手入力・CSV)',
  external_sync: '他システム連携 (POS・食券機等)',
};
const METHOD_DESCRIPTIONS: Record<VisitorCountMethod, string> = {
  menu_derive: '来客数に含めるメニューカテゴリを選択すると、そのカテゴリの販売数量から自動算出します。',
  manual_input: '手入力画面またはCSV取込で日次の来客数を登録します。性別・年代等の属性も記録できます。',
  external_sync: '将来対応予定です。現時点では選択できません。',
};
const DERIVE_RULE_LABELS: Record<DeriveRule, string> = {
  sum_main_only: '主食カテゴリのみ合算 (1客=1品を想定)',
  sum_all_included: '主食+副次カテゴリを合算 (副菜のみの注文も1人とカウント)',
  max_included: '対象カテゴリ内の最大販売数を採用 (1オーダー複数カテゴリの重複を回避)',
};
const SOURCE_TYPE_LABELS: Record<string, string> = {
  manual: '手入力', csv: 'CSV取込', pos: 'POS連携', ticket_machine: '食券機連携', menu_derive: 'メニュー算出',
};

export function VisitorCountSettingsPage() {
  const qc = useQueryClient();
  const roots = useScopeStore((s) => s.roots);
  const selectedNodeId = useScopeStore((s) => s.selectedNodeId);
  const nodeId = selectedNodeId ?? roots[0]?.node_id ?? '';
  const currentNodeName = findNodeInTree(roots, nodeId)?.name ?? null;

  const { data: tenantSettings, isLoading: loadingTenant } = useQuery<TenantSettings>({
    queryKey: ['tenant-settings'], queryFn: () => apiFetch<TenantSettings>('/tenant-settings'),
  });
  const { data: categories = [], isLoading: loadingCategories } = useQuery<CategoryRole[]>({
    queryKey: ['visitor-count-categories', nodeId],
    queryFn: () => apiFetch<CategoryRole[]>(`/config/menu-categories/visitor-roles?nodeId=${nodeId}`),
    enabled: !!nodeId,
  });
  const { data: sourcePriority, isLoading: loadingPriority } = useQuery<SourcePriorityResponse>({
    queryKey: ['visitor-count-source-priority', nodeId],
    queryFn: () => apiFetch<SourcePriorityResponse>(`/config/visitor-count/source-priority?nodeId=${nodeId}`),
    enabled: !!nodeId,
  });

  const [method, setMethod] = useState<VisitorCountMethod>('menu_derive');
  const [deriveRule, setDeriveRule] = useState<DeriveRule>('sum_main_only');
  const [priorityOrder, setPriorityOrder] = useState<string[]>([]);
  // 算出方式変更の確認ダイアログ (Designレビュー CRITICAL対応: 既存の算出値が無警告で0化する事故を防ぐ)
  const [pendingMethod, setPendingMethod] = useState<VisitorCountMethod | null>(null);

  useEffect(() => {
    if (tenantSettings?.visitor_count_config) {
      setMethod(tenantSettings.visitor_count_config.method);
      setDeriveRule(tenantSettings.visitor_count_config.derive_rule);
    }
  }, [tenantSettings]);

  useEffect(() => {
    if (sourcePriority) setPriorityOrder(sourcePriority.source_priority);
  }, [sourcePriority]);

  const saveConfigMut = useMutation({
    mutationFn: (v: VisitorCountConfig) =>
      apiFetch('/config/visitor-count/tenant', { method: 'PUT', body: JSON.stringify(v) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tenant-settings'] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const saveRoleMut = useMutation({
    mutationFn: (v: { categoryCode: string; role: 'main' | 'included' | 'excluded' | null }) =>
      apiFetch(`/config/menu-categories/${v.categoryCode}/visitor-role/${nodeId}`, {
        method: 'PUT', body: JSON.stringify({ visitor_count_role: v.role }),
      }),
    onSuccess: (_data, v) => {
      qc.invalidateQueries({ queryKey: ['visitor-count-categories', nodeId] });
      toast.success(v.role === null ? '継承に戻しました' : '保存しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const savePriorityMut = useMutation({
    mutationFn: (order: string[]) =>
      apiFetch(`/config/visitor-count/source-priority/${nodeId}`, {
        method: 'PUT', body: JSON.stringify({ source_priority: order }),
      }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['visitor-count-source-priority', nodeId] }); toast.success('保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました'),
  });

  const moveSourcePriority = (index: number, direction: -1 | 1) => {
    const next = [...priorityOrder];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    setPriorityOrder(next);
  };

  // 未保存の並び替えがあるか (Testレビュー MEDIUM対応: 保存前にページ離脱すると変更が消える)
  const hasUnsavedPriorityChange = !!sourcePriority
    && JSON.stringify(sourcePriority.source_priority) !== JSON.stringify(priorityOrder);
  useEffect(() => {
    if (!hasUnsavedPriorityChange) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedPriorityChange]);

  const codelessCategories = useMemo(() => categories.filter((c) => !c.category_code), [categories]);

  if (loadingTenant) return <div className="text-neutral-sub">読み込み中…</div>;

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-h2">来客数管理</h2>
        <p className="text-sm text-neutral-sub mt-1">
          来客数の算出方式を設定します。業態や運用形態に合わせて、メニュー算出・手入力・他システム連携から選択できます。
        </p>
      </div>

      {/* 算出方式 (テナント全体に適用、ノードによらず共通) */}
      <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-3">
        <h3 className="text-sm font-bold text-neutral-text">算出方式</h3>
        <p className="text-xs text-neutral-sub">テナント全体 (全ブランド・全店舗) に共通で適用されます。</p>
        <div className="space-y-2">
          {(Object.keys(METHOD_LABELS) as VisitorCountMethod[]).map((m) => (
            <label key={m} className={`flex items-start gap-2 p-2 rounded border ${
              method === m ? 'border-brand-red bg-danger-bg/20' : 'border-neutral-line'
            } ${m === 'external_sync' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
              <input
                type="radio" name="visitor_count_method" className="mt-1"
                checked={method === m} disabled={m === 'external_sync'}
                onChange={() => {
                  // 既に保存済みの方式から別方式へ切り替える場合のみ警告 (新規設定時は不要)
                  if (tenantSettings?.visitor_count_config?.method && tenantSettings.visitor_count_config.method !== m) {
                    setPendingMethod(m);
                  } else {
                    setMethod(m);
                  }
                }}
              />
              <span>
                <span className="block text-sm font-semibold text-neutral-text">{METHOD_LABELS[m]}</span>
                <span className="block text-xs text-neutral-sub mt-0.5">{METHOD_DESCRIPTIONS[m]}</span>
              </span>
            </label>
          ))}
        </div>

        {method === 'menu_derive' && (
          <div className="pt-2 border-t border-neutral-line">
            <label className="block text-sm text-neutral-sub mb-2">メニュー算出ルール</label>
            <div className="space-y-2">
              {(Object.keys(DERIVE_RULE_LABELS) as DeriveRule[]).map((r) => (
                <label key={r} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="derive_rule" checked={deriveRule === r} onChange={() => setDeriveRule(r)} />
                  {DERIVE_RULE_LABELS[r]}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="pt-2 flex justify-end">
          <button type="button" disabled={saveConfigMut.isPending}
            onClick={() => saveConfigMut.mutate({ method, derive_rule: deriveRule })}
            className="px-4 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover disabled:opacity-50">
            保存
          </button>
        </div>
      </div>

      {/* メニューカテゴリ役割設定 */}
      {method === 'menu_derive' && (
        <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-3">
          <h3 className="text-sm font-bold text-neutral-text">メニューカテゴリ役割設定</h3>
          <p className="text-xs text-neutral-sub">
            来客数のカウントに使うカテゴリを選択します。「主食」は1客1品を想定、「副次」は来客判定の補助に使います。
            {currentNodeName && <>現在「<span className="font-semibold">{currentNodeName}</span>」ノードの設定を表示しています。上位ノード (会社・ブランド) の設定を上書きできます。</>}
          </p>
          {codelessCategories.length > 0 && (
            <div className="text-xs bg-alert-bg text-alert-text border border-alert-border/40 rounded px-2 py-1.5">
              {codelessCategories.length} 件のカテゴリはコード未設定のため役割を設定できません (マスタ管理でコードを設定してください)。
            </div>
          )}
          {loadingCategories ? (
            <div className="text-neutral-sub text-sm">読み込み中…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-neutral-sub border-b border-neutral-line">
                    <th scope="col" className="py-1.5 pr-2">カテゴリ</th>
                    <th scope="col" className="py-1.5 pr-2">役割</th>
                    <th scope="col" className="py-1.5 pr-2">設定状況</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat) => (
                    <tr key={cat.menu_category_id} className="border-b border-neutral-line last:border-0">
                      <td className="py-1.5 pr-2">{cat.category_name}</td>
                      <td className="py-1.5 pr-2">
                        <div className="relative inline-block">
                          <select
                            disabled={!cat.category_code}
                            value={cat.visitor_count_role ?? 'excluded'}
                            onChange={(e) => {
                              if (!cat.category_code) return;
                              saveRoleMut.mutate({
                                categoryCode: cat.category_code,
                                role: e.target.value as 'main' | 'included' | 'excluded',
                              });
                            }}
                            className="appearance-none min-h-[44px] sm:min-h-0 px-2 py-1 pr-7 rounded border border-neutral-line text-sm bg-white disabled:opacity-40 focus:outline-none focus:shadow-focus-ring"
                          >
                            <option value="excluded">対象外</option>
                            <option value="main">主食</option>
                            <option value="included">副次</option>
                          </select>
                          <ChevronDown size={14} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-neutral-sub" aria-hidden />
                        </div>
                      </td>
                      <td className="py-1.5 pr-2">
                        {!cat.category_code ? null : cat.is_override ? (
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 text-xs text-neutral-sub">
                              <PenLine size={12} aria-hidden /> このノードで設定
                            </span>
                            <button
                              type="button"
                              onClick={() => saveRoleMut.mutate({ categoryCode: cat.category_code!, role: null })}
                              className="inline-flex items-center gap-1 min-h-[44px] sm:min-h-0 px-2 py-1 text-xs text-neutral-sub hover:text-neutral-text underline underline-offset-2"
                            >
                              <RotateCcw size={12} aria-hidden /> 継承に戻す
                            </button>
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-neutral-sub">
                            <Link2 size={12} aria-hidden />
                            {cat.inherited_from_node_name ? `「${cat.inherited_from_node_name}」から継承` : '既定値 (対象外)'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ソース優先順位 */}
      <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-3">
        <h3 className="text-sm font-bold text-neutral-text">ソース優先順位</h3>
        <p className="text-xs text-neutral-sub">
          同じ日に複数の来客数データ (手入力・CSV・POS等) が存在する場合、上にあるソースを優先して集計します。
          {currentNodeName && <>「<span className="font-semibold">{currentNodeName}</span>」ノードの設定です (上位ノードから継承、未設定なら上書き可)。</>}
          {sourcePriority?.is_default && ' (現在は既定の優先順位を使用中)'}
        </p>
        {/* 抽象的な順位設定だけだと影響がイメージしづらいため、最優先ソースを具体的に明示する
         (Designレビュー MEDIUM対応: Nielsen H2 real world match) */}
        {priorityOrder.length > 0 && (
          <p className="text-xs text-neutral-sub bg-surface-sunken rounded px-2 py-1.5">
            現在の設定では、同日に複数データがある場合「{SOURCE_TYPE_LABELS[priorityOrder[0]!] ?? priorityOrder[0]}」を優先して採用します。
          </p>
        )}
        {loadingPriority ? (
          <div className="text-neutral-sub text-sm">読み込み中…</div>
        ) : (
          <ol className="space-y-1">
            {priorityOrder.map((s, i) => (
              <li key={s} className="flex items-center justify-between gap-2 px-3 py-1.5 rounded border border-neutral-line text-sm">
                <span>{i + 1}. {SOURCE_TYPE_LABELS[s] ?? s}</span>
                <span className="flex gap-1">
                  <button type="button" disabled={i === 0} onClick={() => moveSourcePriority(i, -1)}
                    aria-label={`${SOURCE_TYPE_LABELS[s] ?? s}を上へ`}
                    className="min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:px-2 sm:py-0.5 flex items-center justify-center rounded border border-neutral-line text-xs disabled:opacity-30">↑</button>
                  <button type="button" disabled={i === priorityOrder.length - 1} onClick={() => moveSourcePriority(i, 1)}
                    aria-label={`${SOURCE_TYPE_LABELS[s] ?? s}を下へ`}
                    className="min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:px-2 sm:py-0.5 flex items-center justify-center rounded border border-neutral-line text-xs disabled:opacity-30">↓</button>
                </span>
              </li>
            ))}
          </ol>
        )}
        <div className="pt-1 flex items-center justify-end gap-2">
          {hasUnsavedPriorityChange && (
            <span className="text-xs text-alert-text">未保存の変更があります</span>
          )}
          <button type="button" disabled={savePriorityMut.isPending || priorityOrder.length === 0}
            onClick={() => savePriorityMut.mutate(priorityOrder)}
            className="px-4 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover disabled:opacity-50">
            保存
          </button>
        </div>
      </div>

      {/* 算出方式変更の確認 (Designレビュー CRITICAL対応) */}
      <ConfirmDialog
        open={pendingMethod !== null}
        title="算出方式を変更しますか？"
        destructive
        confirmLabel="変更する"
        onClose={() => setPendingMethod(null)}
        onConfirm={() => {
          if (pendingMethod) setMethod(pendingMethod);
          setPendingMethod(null);
        }}
        message={
          <div className="space-y-2">
            <p>
              算出方式を「{tenantSettings?.visitor_count_config ? METHOD_LABELS[tenantSettings.visitor_count_config.method] : ''}」から
              「{pendingMethod ? METHOD_LABELS[pendingMethod] : ''}」に変更します。
            </p>
            <div className="p-2 rounded bg-alert-bg border border-alert-border/40 text-xs text-alert-text flex items-start gap-1.5">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              変更後は新しい方式でダッシュボードの来客数が再計算されます。旧方式のデータが無効化されるわけではありませんが、表示される数値が変わります。「保存」を押すまでは確定しません。
            </div>
          </div>
        }
      />
    </div>
  );
}
